import React, { useState, useEffect, useRef, memo } from "react";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Badge } from "primereact/badge";
import { Chip } from "primereact/chip";
import { Tag } from "primereact/tag";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { Can } from "../../components/PermissionGate";
import { useAuth } from "../../hooks/useAuth";
import { separateString, toastAlert } from "../../core/utils";
import { AssignmentService } from "../../service/AssignmentService";
import DesignUpload from "./DesignUpload";
import ViewRequest from "./ViewRequest";
import DesignReasign from "./DesignReasign";

const STATUS_CONFIG = {
    SAVED: { text: "SAVED", variant: "info", icon: "pi pi-save" },
    APPROVED: { text: "APPROVED", variant: "success", icon: "pi pi-check-circle" },
    DISAPPROVED: { text: "DISAPPROVED", variant: "danger", icon: "pi pi-times" },
};

const AssignmentsLists = () => {
    // Services and refs
    const dataService = useRef(new AssignmentService());
    const toast = useRef(null);
    const { 0: auth } = useAuth();

    // State management
    const [state, setState] = useState({
        dataList: [],
        loading: false,
        loadingButton: false,
        designers: { items: [] },
        currentDesigner: null,
        expandedRows: [],
        filters: {
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            urgent: { value: null, matchMode: FilterMatchMode.IN },
            status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
            types: { value: null, matchMode: FilterMatchMode.IN },
        },
        globalFilterValue: "",
        modalStates: {
            deleteDialog: false,
            uploadSidebar: false,
            viewSidebar: false,
            reassignModal: false,
        },
        selectedItem: null,
        selectedId: null,
    });

    const { dataList, loading, loadingButton, designers, currentDesigner, expandedRows, filters, globalFilterValue, modalStates, selectedItem, selectedId } = state;

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            setState((prev) => ({ ...prev, loading: true }));

            const [assignments, designersList] = await Promise.all([
                dataService.current
                    .getList()
                    .then(safeDataTransform)
                    .catch(() => []),
                dataService.current.getDesigners().catch(() => ({ items: [] })),
            ]);

            setState((prev) => ({
                ...prev,
                dataList: assignments,
                designers: designersList,
                loading: false,
            }));
        } catch (error) {
            handleError(error, "Failed to load initial data");
        }
    };

    const safeDataTransform = (response) => {
        if (!Array.isArray(response)) return [];

        return response.map((item) => ({
            ...item,
            _RESOURCES: item._RESOURCES || [],
            _UPLOADED: item._UPLOADED || [],
            _FORMATS: item._FORMATS || [],
            _FILES: item._FILES || [],
            _NOTIFY: item._NOTIFY || false,
        }));
    };

    const handleError = (error, defaultMessage = "An error occurred") => {
        console.error(error);
        setState((prev) => ({ ...prev, loading: false, loadingButton: false }));
        toastAlert("error", error.message || defaultMessage, toast);
    };

    // Modal handlers
    const toggleModal = (modalName, isOpen, data = null) => {
        
        setState((prev) => ({
            ...prev,
            modalStates: { ...prev.modalStates, [modalName]: isOpen },
            ...(data && { selectedItem: data }),
            ...(data?._IDASSIGNMET && { selectedId: data._IDASSIGNMET }),
            ...(data?._DESIGNER && { currentDesigner: data._DESIGNER }),
        }));
    };

    // Data operations
    const deleteAssignment = async () => {
        try {
            setState((prev) => ({ ...prev, loading: true, loadingButton: true }));
            const result = await dataService.current.deleteData(selectedId);

            if (result?._SUCCESS) {
                setState((prev) => ({
                    ...prev,
                    dataList: prev.dataList.filter((item) => item._ID !== selectedItem?._ID),
                    loading: false,
                    loadingButton: false,
                    modalStates: { ...prev.modalStates, deleteDialog: false },
                }));
                toastAlert("success", result._MESSAGE, toast);
            } else {
                toastAlert("warn", result?._MESSAGE || "Deletion failed", toast);
            }
        } catch (error) {
            handleError(error, "Failed to delete assignment");
        }
    };

    // Filter handlers
    const handleGlobalFilterChange = (e) => {
        const value = e.target.value;
        setState((prev) => ({
            ...prev,
            filters: {
                ...prev.filters,
                global: { ...prev.filters.global, value },
            },
            globalFilterValue: value,
        }));
    };

    const STATUS_CONFIG = {
        SAVED: { text: "SAVED", variant: "info", icon: "pi pi-save" },
        APPROVED: { text: "APPROVED", variant: "success", icon: "pi pi-check-circle" },
        DISAPPROVE: { text: "DISAPPROVED", variant: "danger", icon: "pi pi-times" },
    };

    const renderStatusBadge = (rowData) => {
        if (!rowData?._UPLOADED?.length) return null;

        return (
            <div className="flex flex-wrap gap-1">
                {rowData._UPLOADED.map((upload) => {
                    const lastStatus = upload._FILESTATUS?.slice(-1)[0];
                    const status = STATUS_CONFIG[lastStatus] || STATUS_CONFIG.DISAPPROVE;

                    const socialNetwork = upload._FILEFORMAT?.split(' ')[0]?.toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) || 'Social';

                    return (
                        <Tag
                            key={upload._FILEID}
                            value={socialNetwork}
                            icon={status.icon}
                            severity={status.variant}
                            className="text-xs"
                        />
                    );
                })}
            </div>
        );
    };

    const renderFormats = (formats) => {
        if (!formats?.length) return "No formats";

        return formats.map((format, i) => <Chip key={i} label={`${format.socialMedia}: ${format.socialFormat?.join(", ") || "No formats"}`} className="mr-2 mb-2" />);
    };

    const updateNotify = (updatedItem) => {
        setState((prev) => ({
            ...prev,
            dataList: prev.dataList.map((item) => (item._ID === updatedItem._ID ? { ...item, _NOTIFY: updatedItem._NOTIFY } : item)),
        }));
    };

    const renderTextColumn = (value, formatter = null) => <>{formatter ? formatter(value) : value || "N/A"}</>;

    const renderActionButtons = (rowData) => {
        const isAuthorized = Can("assignments.read");
        const canDelete = Can("assignments.delete");
        const hasUploads = rowData?._UPLOADED?.length > 0;

        return (
            <div className="actions">
                <Button tooltip="Re-asign" icon="pi pi-pencil" className="p-button-rounded p-button-plain p-button-text mr-1 mb-1" onClick={() => toggleModal("reassignModal", true, rowData)} />

                <Button tooltip="View" icon="pi pi-eye" className="p-button-rounded p-button-plain p-button-text mr-1 mb-1" onClick={() => toggleModal("viewSidebar", true, rowData)} />

                {isAuthorized && <Button tooltip="Design Upload" icon="pi pi-image" className="p-button-rounded p-button-plain p-button-text mr-1 mb-1" onClick={() => toggleModal("uploadSidebar", true, rowData)} />}

                {canDelete && <Button tooltip="Delete" icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text mr-1 mb-1" onClick={() => toggleModal("deleteDialog", true, rowData)} disabled={hasUploads} />}
            </div>
        );
    };

    const renderGroupHeader = (data) => {
        if (!data) return null;

        const campaignName = data._IDCAMPAIGN !== null ? data._CAMPAIGN : "External Assignments";

        const assignmentCount = dataList.filter((list) => list._CAMPAIGN === data._CAMPAIGN).length;
        const colorClass = data._IDCAMPAIGN !== null ? "text-green-700" : "text-blue-700";

        return (
            <>
                <span className={`font-semibold capitalize text-lg ${colorClass}`}>{campaignName}</span>
                <div className="flex align-items-center flex-wrap">
                    <Chip
                        className="mr-3"
                        template={
                            <span className="p-chip p-component">
                                <FontAwesomeIcon icon={faUser} className="p-chip-icon" />
                                <span className="p-chip-text">Total Assignments</span>
                                <Badge value={assignmentCount} className="ml-2" />
                            </span>
                        }
                    />
                </div>
            </>
        );
    };

    const renderTableHeader = () => (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">Assignments Lists</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" value={globalFilterValue} onChange={handleGlobalFilterChange} placeholder="Search..." />
            </span>
        </div>
    );

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />

                    <DataTable
                        value={dataList}
                        rowGroupMode="subheader"
                        groupRowsBy="_CAMPAIGN"
                        sortMode="single"
                        sortField="_CAMPAIGN"
                        sortOrder={1}
                        responsiveLayout="scroll"
                        expandableRowGroups
                        expandedRows={expandedRows}
                        onRowToggle={(e) => setState((prev) => ({ ...prev, expandedRows: e.data }))}
                        rowGroupHeaderTemplate={renderGroupHeader}
                        header={renderTableHeader()}
                        rowClassName={(rowData) => ({ "row-assigned": rowData._NOTIFY === true })}
                        loading={loading}
                        filters={filters}
                        filterDisplay="menu"
                        globalFilterFields={["_CONCEPT", "_REGISTER", "_DESIGNER", "_SUPERVISOR", "_DATEASSIGN"]}
                    >
                        <Column field="_CONCEPT" header="Text" body={(rowData) => renderTextColumn(rowData._CONCEPT, separateString)} style={{ width: "15%" }} />
                        <Column field="_REGISTER" header="Register by" body={(rowData) => renderTextColumn(rowData._REGISTER)} style={{ width: "15%" }} />
                        <Column field="_DESIGNER" header="Assigned" body={(rowData) => renderTextColumn(rowData._DESIGNER)} style={{ width: "15%" }} />
                        <Column field="_SUPERVISOR" header="Supervisor" body={(rowData) => renderTextColumn(rowData._SUPERVISOR)} style={{ width: "15%" }} />
                        <Column field="status" header="Status" body={renderStatusBadge} style={{ width: "10%" }} />
                        <Column field="_DATEASSIGN" header="Date Assignment" body={(rowData) => renderTextColumn(rowData._DATEASSIGN)} style={{ width: "15%" }} />
                        <Column body={renderActionButtons} style={{ width: "25%", textAlign: "center" }} />
                    </DataTable>

                    {/* Modals */}
                    <Dialog
                        visible={modalStates.deleteDialog}
                        style={{ width: "450px" }}
                        header="Confirm"
                        modal
                        footer={
                            <>
                                <Button label="No" disabled={loadingButton} icon="pi pi-times" className="p-button-text" onClick={() => toggleModal("deleteDialog", false)} />
                                <Button label="Yes" loading={loadingButton} icon="pi pi-check" className="p-button-text" onClick={deleteAssignment} />
                            </>
                        }
                        onHide={() => toggleModal("deleteDialog", false)}
                    >
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
                            <span>
                                Do you want to remove this <b>Assignment</b>?
                            </span>
                        </div>
                    </Dialog>

                    <ViewRequest visible={modalStates.viewSidebar} hide={() => toggleModal("viewSidebar", false)} items={selectedItem} />

                    <DesignUpload
                        visible={modalStates.uploadSidebar}
                        hide={() => toggleModal("uploadSidebar", false)}
                        items={selectedItem || {}}
                        updateData={(updatedItem) => {
                            setState((prev) => ({
                                ...prev,
                                dataList: prev.dataList.map((item) => (item._ID === updatedItem._ID ? updatedItem : item)),
                            }));
                        }}
                        updateNotify={updateNotify}
                    />

                    <DesignReasign
                        idRequest={selectedId}
                        onVisible={modalStates.reassignModal}
                        onHide={() => toggleModal("reassignModal", false)}
                        designers={designers.items || []}
                        currentDesigner={currentDesigner}
                        updateDesignerAssignment={(updatedItem) => {
                            setState((prev) => ({
                                ...prev,
                                dataList: prev.dataList.map((item) => (item._ID === updatedItem._ID ? updatedItem : item)),
                            }));
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default memo(AssignmentsLists);
